import React from 'react';
import SEO from '../components/SEO';

const AboutUs = () => {
    return (
        <>
            <SEO
                title="About Us - Free AI Math Solver | FreeImageMathSolver"
                description="Learn about FreeImageMathSolver - the free AI-powered math problem solver helping students worldwide. Our mission is to make math education accessible to everyone."
                keywords="about math solver, AI math education, free math help, math learning platform, educational technology"
                canonical="/about"
            />

            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Hero Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                            About <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">FreeImageMathSolver</span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Empowering students worldwide with free, AI-powered math solutions
                        </p>
                    </div>

                    {/* Mission Section */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
                                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                                    At FreeImageMathSolver, we believe that quality math education should be accessible to everyone, regardless of their location or financial situation.
                                </p>
                                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                                    Our mission is to break down barriers to math learning by providing instant, accurate, and detailed solutions to math problems through cutting-edge AI technology.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    We're committed to helping students understand not just the answer, but the complete problem-solving process through step-by-step explanations.
                                </p>
                            </div>
                            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl p-8">
                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-2">Free Education</h3>
                                            <p className="text-gray-700">100% free with no hidden costs or subscriptions</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-2">Instant Results</h3>
                                            <p className="text-gray-700">Get solutions in seconds, not hours</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-2">Global Access</h3>
                                            <p className="text-gray-700">Available worldwide, 24/7</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Technology Section */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Technology</h2>
                        <p className="text-lg text-gray-700 mb-8 text-center max-w-3xl mx-auto">
                            We leverage state-of-the-art artificial intelligence and machine learning to provide accurate, reliable math solutions
                        </p>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-gray-900 mb-2">AI Recognition</h3>
                                <p className="text-gray-600">Advanced OCR and computer vision to read handwritten and typed math</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-gray-900 mb-2">Smart Algorithms</h3>
                                <p className="text-gray-600">Sophisticated mathematical algorithms for accurate problem-solving</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-gray-900 mb-2">Cloud Processing</h3>
                                <p className="text-gray-600">Fast, scalable cloud infrastructure for instant results</p>
                            </div>
                        </div>
                    </div>

                    {/* Stats Section */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-8 md:p-12 mb-12 text-white">
                        <h2 className="text-3xl font-bold mb-8 text-center">Our Impact</h2>
                        <div className="grid md:grid-cols-4 gap-8 text-center">
                            <div>
                                <div className="text-4xl md:text-5xl font-bold mb-2">100K+</div>
                                <div className="text-indigo-100">Problems Solved</div>
                            </div>
                            <div>
                                <div className="text-4xl md:text-5xl font-bold mb-2">50K+</div>
                                <div className="text-indigo-100">Active Users</div>
                            </div>
                            <div>
                                <div className="text-4xl md:text-5xl font-bold mb-2">150+</div>
                                <div className="text-indigo-100">Countries</div>
                            </div>
                            <div>
                                <div className="text-4xl md:text-5xl font-bold mb-2">99%</div>
                                <div className="text-indigo-100">Accuracy Rate</div>
                            </div>
                        </div>
                    </div>

                    {/* Values Section */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Core Values</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="flex gap-4">
                                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2">Accuracy & Reliability</h3>
                                    <p className="text-gray-600">We ensure every solution is mathematically correct and thoroughly verified</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2">Privacy & Security</h3>
                                    <p className="text-gray-600">Your data is secure and never shared with third parties</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2">Student-Focused</h3>
                                    <p className="text-gray-600">Every feature is designed with student success in mind</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2">Continuous Innovation</h3>
                                    <p className="text-gray-600">We're constantly improving our AI to serve you better</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* JSON-LD Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "FreeImageMathSolver",
                    "url": "https://freeimagemathsolver.online",
                    "logo": "https://freeimagemathsolver.online/logo.png",
                    "description": "Free AI-powered math problem solver providing step-by-step solutions for students worldwide",
                    "foundingDate": "2026",
                    "sameAs": []
                })}
            </script>
        </>
    );
};

export default AboutUs;
